<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="7">
            <h1 class="h4 mb-1 font-weight-bold">Packages</h1>
            <p class="text-muted mb-0">Subscription plans tenants can sign up for</p>
          </CCol>
          <CCol md="5" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              :disabled="loading"
              @click="refreshTable"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add package
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardBody>
        <div v-if="loading && !items.length" class="text-center p-5">
          <CSpinner color="primary" />
        </div>
        <div v-else-if="loadError" class="text-center p-5">
          <CIcon name="cil-warning" size="xl" class="text-danger mb-3" />
          <h2 class="h6 font-weight-bold">Couldn't load packages</h2>
          <p class="text-muted">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshTable">Retry</CButton>
        </div>
        <div v-else-if="!items.length" class="text-center p-5">
          <CIcon name="cil-layers" size="xl" class="text-muted mb-3" />
          <h2 class="h6 font-weight-bold">No packages yet</h2>
          <p class="text-muted">Add a package so tenants can subscribe to it.</p>
          <CButton color="primary" @click="addNew">Add package</CButton>
        </div>
        <template v-else>
          <!-- Desktop table -->
          <div class="d-none d-lg-block">
            <CDataTable
              :items="items"
              :fields="fields"
              column-filter
              items-per-page-select
              :items-per-page="25"
              hover
              sorter
              pagination
              :loading="loading"
            >
              <template #price="{ item }">
                <td class="text-right">RM {{ formatMyr(item.price) }}</td>
              </template>
              <template #referrerCommission="{ item }">
                <td class="text-right">RM {{ formatMyr(item.referrerCommission) }}</td>
              </template>
              <template #isActive="{ item }">
                <td>
                  <CBadge :color="item.isActive ? 'success' : 'secondary'">
                    {{ item.isActive ? "Active" : "Inactive" }}
                  </CBadge>
                </td>
              </template>
              <template #autoRenew="{ item }">
                <td>{{ item.autoRenew ? "Yes" : "No" }}</td>
              </template>
              <template #actions="{ item }">
                <td class="text-nowrap">
                  <CButton
                    color="light"
                    size="sm"
                    class="mr-1"
                    :aria-label="`Edit ${item.name}`"
                    @click="onEdit(item)"
                  >
                    <CIcon name="cil-pencil" />
                  </CButton>
                  <CButton
                    color="light"
                    size="sm"
                    :aria-label="`Delete ${item.name}`"
                    @click="showDeleteConfirmation(item)"
                  >
                    <CIcon name="cil-trash" class="text-danger" />
                  </CButton>
                </td>
              </template>
            </CDataTable>
          </div>

          <!-- Mobile card list -->
          <div class="d-lg-none">
            <div
              v-for="item in items"
              :key="item.id"
              class="border rounded p-3 mb-2"
            >
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <div class="font-weight-bold">{{ item.name }}</div>
                  <div class="small text-muted">
                    RM {{ formatMyr(item.price) }} · {{ item.totalMonth }} months
                  </div>
                </div>
                <CBadge :color="item.isActive ? 'success' : 'secondary'">
                  {{ item.isActive ? "Active" : "Inactive" }}
                </CBadge>
              </div>
              <div class="mt-2">
                <CButton color="light" size="sm" class="mr-2" @click="onEdit(item)">
                  <CIcon name="cil-pencil" /> Edit
                </CButton>
                <CButton color="light" size="sm" @click="showDeleteConfirmation(item)">
                  <CIcon name="cil-trash" class="text-danger" /> Delete
                </CButton>
              </div>
            </div>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <CModal
      :title="`Delete package ${itemToDelete.name || ''}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
    >
      The package will be removed and tenants can no longer subscribe to it. This can't be undone.
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">
          Keep it
        </CButton>
        <CButton color="danger" :disabled="deleting" @click="doDelete">
          Delete package
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import PackageApi from "@/lib/packageApi";
import { formatMyr } from "@/utils/money";

const fields = [
  { key: "name", label: "Name" },
  { key: "price", label: "Price", _classes: "text-right" },
  { key: "referrerCommission", label: "Referrer commission", _classes: "text-right" },
  { key: "totalMonth", label: "Months" },
  { key: "isActive", label: "Status" },
  { key: "autoRenew", label: "Auto renew" },
  { key: "actions", label: "", _style: "width:1%", sorter: false, filter: false },
];

export default {
  name: "PackageList",
  data() {
    return {
      loading: true,
      loadError: false,
      deleting: false,
      items: [],
      fields,
      api: new PackageApi(),
      warningModal: false,
      itemToDelete: {},
    };
  },
  mounted() {
    this.refreshTable();
  },
  methods: {
    formatMyr,
    refreshTable() {
      var self = this;
      self.loading = true;
      self.loadError = false;
      self.api
        .getList()
        .then((response) => {
          self.items = response.result || [];
        })
        .catch((error) => {
          console.error(error);
          self.loadError = true;
        })
        .finally(() => {
          self.loading = false;
        });
    },
    onEdit(item) {
      this.$router.push({ path: `/admins/Package/${item.id}` });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    doDelete() {
      var self = this;
      self.deleting = true;
      self.api
        .delete(self.itemToDelete.id)
        .then(() => {
          self.$toast.success("Package deleted");
          self.warningModal = false;
          self.itemToDelete = {};
          self.refreshTable();
        })
        .catch(({ data }) => {
          self.$toast.error(
            "Couldn't delete the package. " + helper.getErrorMessage(data)
          );
        })
        .finally(() => {
          self.deleting = false;
        });
    },
    addNew() {
      this.$router.push({ path: "/admins/Package" });
    },
  },
};
</script>
